import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
  Collapse,
  Stack,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  ContentCopy as CopyIcon,
  Settings as SettingsIcon,
  Terminal as TerminalIcon,
  Web as WebIcon,
  Extension as ExtensionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material'
import { mcpApi } from '../services/mcpApi'

function CodeBlock({ children, dark = true }) {
  const cls = dark
    ? 'bg-gray-900 text-green-400 p-3 rounded font-mono text-sm overflow-x-auto'
    : 'bg-gray-100 text-gray-800 p-3 rounded font-mono text-sm overflow-x-auto'
  return <Box className={cls}><pre className="m-0 whitespace-pre-wrap break-all">{children}</pre></Box>
}

function CopyableField({ label, value, fieldKey, copiedField, onCopy }) {
  return (
    <Box>
      <Typography variant="caption" className="text-gray-500 uppercase tracking-wide">{label}</Typography>
      <Box className="flex items-center gap-2 bg-gray-50 p-2 rounded mt-1">
        <code className="text-sm break-all flex-1">{value}</code>
        <Button size="small" startIcon={<CopyIcon />} onClick={() => onCopy(value, fieldKey)}>
          {copiedField === fieldKey ? 'Skopiowano!' : 'Kopiuj'}
        </Button>
      </Box>
    </Box>
  )
}

export default function McpSettingsPage() {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newClientName, setNewClientName] = useState('')
  const [createdClient, setCreatedClient] = useState(null)
  const [copiedField, setCopiedField] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [tunnelOpen, setTunnelOpen] = useState(false)
  const [ngrokUrl, setNgrokUrl] = useState('')
  const [auditOpen, setAuditOpen] = useState(false)
  const [auditEvents, setAuditEvents] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)

  // VITE_API_URL może być absolutny (http://localhost:8089) albo relatywny (np. /api).
  // Dla wewnętrznych wywołań API używamy `apiBaseUrl`. Ale **MCP / OAuth muszą być
  // na rooku domeny** (nie pod /api/) — Claude AI fetchuje /.well-known/* z root
  // hosta per RFC9728, a legacy klienty próbują /authorize / /token / /revoke też
  // na rooku. Reverse-proxy w prod (nginx.conf) forwarduje te ścieżki do backendu.
  const rawApiBase = import.meta.env.VITE_API_URL || 'http://localhost:8089'
  const apiBaseUrl = /^https?:\/\//i.test(rawApiBase)
    ? rawApiBase
    : `${window.location.origin}${rawApiBase.startsWith('/') ? rawApiBase : `/${rawApiBase}`}`

  // MCP base — zawsze origin (bez /api prefix). Tylko gdy backend jest absolutny
  // i NIE jest pod tym samym originem (lokalny dev: localhost:8089) używamy go
  // bezpośrednio.
  const mcpBaseUrl = (() => {
    if (typeof window === 'undefined') return apiBaseUrl
    if (apiBaseUrl.startsWith(window.location.origin)) return window.location.origin
    return apiBaseUrl
  })()
  const mcpSseUrl = `${mcpBaseUrl}/mcp/sse`
  const mcpResource = `${mcpBaseUrl}/mcp`

  useEffect(() => {
    loadClients()
  }, [])

  const loadClients = async () => {
    try {
      setLoading(true)
      const res = await mcpApi.getClients()
      setClients(res.data || [])
      setError(null)
    } catch (err) {
      setError(err.response?.data?.detail || 'Błąd podczas ładowania klientów OAuth')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!newClientName.trim()) return
    try {
      const res = await mcpApi.createClient(newClientName.trim())
      setCreatedClient(res.data)
      setCreateDialogOpen(false)
      setNewClientName('')
      loadClients()
    } catch (err) {
      setError(err.response?.data?.detail || 'Błąd podczas tworzenia klienta')
    }
  }

  const handleRevoke = async (clientId) => {
    if (!confirm('Czy na pewno chcesz odwołać tego klienta? Wszystkie sesje używające go przestaną działać.')) return
    try {
      await mcpApi.revokeClient(clientId)
      loadClients()
    } catch (err) {
      setError(err.response?.data?.detail || 'Błąd podczas odwoływania klienta')
    }
  }

  const handlePermanentDelete = async (clientId, name) => {
    if (!confirm(`Trwale usunąć klienta „${name}”? Akcja nieodwracalna — usunięte zostaną także wszystkie tokeny z nim związane.`)) return
    try {
      await mcpApi.permanentlyDeleteClient(clientId)
      loadClients()
    } catch (err) {
      setError(err.response?.data?.detail || 'Błąd podczas usuwania klienta')
    }
  }

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const loadAudit = async () => {
    try {
      setAuditLoading(true)
      const res = await mcpApi.getAuditEvents(80)
      setAuditEvents(res.data || [])
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się pobrać logu zdarzeń')
    } finally {
      setAuditLoading(false)
    }
  }

  useEffect(() => {
    if (auditOpen) loadAudit()
  }, [auditOpen])

  const handleInitSession = async () => {
    if (!ngrokUrl.trim()) return
    try {
      await mcpApi.initSession(ngrokUrl.trim())
      setError(null)
      alert('Sesja zainicjalizowana. Możesz teraz autoryzować Claude Code.')
    } catch (err) {
      setError(err.response?.data?.detail || 'Błąd inicjalizacji sesji')
    }
  }

  return (
    <Box className="p-6 max-w-5xl mx-auto">
      <Typography variant="h4" className="mb-1 font-bold text-gray-800 flex items-center gap-2">
        <SettingsIcon fontSize="large" />
        MCP — integracja z AI
      </Typography>
      <Typography variant="body2" className="text-gray-600 mb-6">
        KAM-SFA udostępnia surowe dane analityczne (sprzedaż, salony, agenci, regiony) przez Model Context Protocol.
        Wybierz narzędzie poniżej, aby zobaczyć krok-po-kroku jak je podłączyć.
      </Typography>

      {error && (
        <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step 1: Clients management */}
      <Paper className="mb-6 p-4 shadow-sm">
        <Box className="flex items-center justify-between mb-3">
          <Box>
            <Typography variant="h6" className="font-semibold text-gray-800">
              Krok 1 — wygeneruj klucze OAuth
            </Typography>
            <Typography variant="body2" className="text-gray-500">
              Każde narzędzie (laptop, urządzenie, integracja) powinno mieć osobny zestaw kluczy.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Nowe połączenie
          </Button>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow className="bg-gray-50">
                <TableCell className="font-semibold">Nazwa</TableCell>
                <TableCell className="font-semibold">Client ID</TableCell>
                <TableCell className="font-semibold">Status</TableCell>
                <TableCell className="font-semibold">Utworzono</TableCell>
                <TableCell className="font-semibold text-right">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-4">Ładowanie…</TableCell>
                </TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-6">
                    Brak połączeń. Kliknij <strong>„Nowe połączenie”</strong>, aby zacząć.
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow key={client.client_id} hover>
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell>
                      <Box className="flex items-center gap-1">
                        <code className="text-xs bg-gray-100 px-1 rounded truncate max-w-[200px]">
                          {client.client_id}
                        </code>
                        <IconButton size="small" onClick={() => copyToClipboard(client.client_id, `id-${client.client_id}`)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                        {copiedField === `id-${client.client_id}` && <Chip size="small" label="OK" color="success" />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {client.is_active ? (
                        <Chip size="small" label="Aktywny" color="success" />
                      ) : (
                        <Chip size="small" label="Odwołany" color="default" />
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600 text-sm">
                      {new Date(client.created_at).toLocaleString('pl-PL')}
                    </TableCell>
                    <TableCell className="text-right">
                      {client.is_active ? (
                        <Tooltip title="Odwołaj dostęp">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRevoke(client.client_id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Usuń trwale (wraz z tokenami)">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handlePermanentDelete(client.client_id, client.name)}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Step 2: Per-tool instructions in tabs */}
      <Paper className="mb-6 shadow-sm">
        <Box className="px-4 pt-3">
          <Typography variant="h6" className="font-semibold text-gray-800">
            Krok 2 — podłącz narzędzie
          </Typography>
          <Typography variant="body2" className="text-gray-500">
            Wybierz to, którego używasz. Każde wymaga skopiowania <strong>Client ID</strong> i <strong>Client Secret</strong> z Kroku 1.
          </Typography>
        </Box>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          className="border-b border-gray-200 px-4"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<WebIcon />} iconPosition="start" label="Claude AI (web/desktop)" />
          <Tab icon={<TerminalIcon />} iconPosition="start" label="Claude Code (CLI)" />
          <Tab icon={<ExtensionIcon />} iconPosition="start" label="Inne / uniwersalne" />
        </Tabs>

        {/* Claude AI tab */}
        <Box hidden={activeTab !== 0} className="p-4">
          <Stepper orientation="vertical" nonLinear>
            <Step active>
              <StepLabel>Otwórz ustawienia connectorów w Claude</StepLabel>
              <StepContent>
                <Typography variant="body2" className="text-gray-700">
                  Claude AI → <strong>Settings</strong> → <strong>Connectors</strong> → <strong>Add custom connector</strong>.
                </Typography>
              </StepContent>
            </Step>
            <Step active>
              <StepLabel>Wklej adres serwera MCP</StepLabel>
              <StepContent>
                <Box className="flex items-center gap-2">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm flex-1 break-all">{mcpSseUrl}</code>
                  <Button size="small" startIcon={<CopyIcon />} onClick={() => copyToClipboard(mcpSseUrl, 'web-url')}>
                    {copiedField === 'web-url' ? 'OK' : 'Kopiuj'}
                  </Button>
                </Box>
              </StepContent>
            </Step>
            <Step active>
              <StepLabel>W „Advanced settings” wklej Client ID i Client Secret</StepLabel>
              <StepContent>
                <Typography variant="body2" className="text-gray-700">
                  Użyj kluczy z Kroku 1. Jeśli nie masz jeszcze klienta — wróć na górę i utwórz nowy.
                </Typography>
              </StepContent>
            </Step>
            <Step active>
              <StepLabel>Kliknij „Add” i zaloguj się na ekranie zgody</StepLabel>
              <StepContent>
                <Typography variant="body2" className="text-gray-700">
                  Claude przekieruje Cię do ekranu autoryzacji KAM-SFA. Po kliknięciu <strong>„Zezwalaj”</strong> integracja jest gotowa.
                </Typography>
              </StepContent>
            </Step>
          </Stepper>
          <Alert severity="info" className="mt-3">
            <strong>Resource URI</strong> (canonical): <code>{mcpResource}</code> — wymagane jeśli Claude poprosi o jego ręczne podanie.
          </Alert>
        </Box>

        {/* Claude Code tab */}
        <Box hidden={activeTab !== 1} className="p-4">
          <Alert severity="info" className="mb-3">
            Najszybszy sposób — komenda CLI (zostaniesz poproszony o Client Secret podczas dodawania):
          </Alert>
          <CodeBlock>
            {`claude mcp add --transport sse \\\n  --client-id TWÓJ_CLIENT_ID \\\n  --client-secret kam-sfa \\\n  ${mcpSseUrl}`}
          </CodeBlock>
          <Box className="mt-3">
            <Button
              size="small"
              startIcon={<CopyIcon />}
              onClick={() => copyToClipboard(`claude mcp add --transport sse --client-id TWÓJ_CLIENT_ID --client-secret kam-sfa ${mcpSseUrl}`, 'cc-cmd')}
            >
              {copiedField === 'cc-cmd' ? 'Skopiowano' : 'Kopiuj komendę'}
            </Button>
          </Box>

          <Typography variant="subtitle2" className="mt-4 mb-2 text-gray-700">
            Albo dodaj ręcznie do <code>~/.claude.json</code>:
          </Typography>
          <CodeBlock dark={false}>{`{
  "mcpServers": {
    "kam-sfa": {
      "type": "sse",
      "url": "${mcpSseUrl}",
      "oauth": {
        "clientId": "YOUR_CLIENT_ID",
        "clientSecret": "YOUR_CLIENT_SECRET",
        "scopes": "mcp:read"
      }
    }
  }
}`}</CodeBlock>

          <Typography variant="body2" className="text-gray-600 mt-3">
            Po dodaniu uruchom <code>claude</code> i wpisz <code>/mcp</code>, aby sprawdzić status połączenia.
            Jeśli kiedykolwiek zobaczysz błąd <em>„Could not find session”</em> — wpisz <code>/mcp</code> ponownie i wybierz <strong>Reconnect</strong>.
          </Typography>

          <Box className="mt-4 border border-gray-200 rounded">
            <Button
              fullWidth
              onClick={() => setTunnelOpen((v) => !v)}
              endIcon={tunnelOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              className="justify-between text-left"
              sx={{ textTransform: 'none', px: 2, py: 1 }}
            >
              <Typography variant="subtitle2" className="text-gray-700">
                Zaawansowane: tunelowanie (ngrok / dev tunnel)
              </Typography>
            </Button>
            <Collapse in={tunnelOpen}>
              <Box className="p-3 border-t border-gray-200">
                <Typography variant="body2" className="text-gray-600 mb-2">
                  Jeśli backend działa lokalnie i wystawiasz go przez tunel HTTPS (np. ngrok), kliknij <strong>„Inicjalizuj sesję”</strong>.
                  Ustawi to cookie sesji dla domeny tunelu — wymagane do consent flow.
                </Typography>
                <Stack direction="row" gap={1}>
                  <TextField
                    size="small"
                    label="URL tunelu"
                    placeholder="https://xxx.ngrok.app"
                    value={ngrokUrl}
                    onChange={(e) => setNgrokUrl(e.target.value)}
                    fullWidth
                  />
                  <Button variant="outlined" onClick={handleInitSession}>Inicjalizuj sesję</Button>
                </Stack>
              </Box>
            </Collapse>
          </Box>
        </Box>

        {/* Other tools tab */}
        <Box hidden={activeTab !== 2} className="p-4">
          <Typography variant="body2" className="text-gray-700 mb-3">
            Każdy klient zgodny z OAuth 2.1 + MCP/SSE może się podłączyć. Skopiuj poniższe wartości:
          </Typography>
          <Stack gap={2}>
            <CopyableField
              label="MCP SSE endpoint"
              value={mcpSseUrl}
              fieldKey="other-sse"
              copiedField={copiedField}
              onCopy={copyToClipboard}
            />
            <CopyableField
              label="Resource (audience)"
              value={mcpResource}
              fieldKey="other-resource"
              copiedField={copiedField}
              onCopy={copyToClipboard}
            />
            <CopyableField
              label="Authorization endpoint"
              value={`${mcpBaseUrl}/mcp/oauth/authorize`}
              fieldKey="other-auth"
              copiedField={copiedField}
              onCopy={copyToClipboard}
            />
            <CopyableField
              label="Token endpoint"
              value={`${mcpBaseUrl}/mcp/oauth/token`}
              fieldKey="other-token"
              copiedField={copiedField}
              onCopy={copyToClipboard}
            />
            <CopyableField
              label="Discovery (Authorization Server Metadata)"
              value={`${mcpBaseUrl}/.well-known/oauth-authorization-server`}
              fieldKey="other-discovery"
              copiedField={copiedField}
              onCopy={copyToClipboard}
            />
          </Stack>
          <Alert severity="info" className="mt-3">
            Wymagane: PKCE S256, grant_type <code>authorization_code</code> oraz <code>refresh_token</code>, scope <code>mcp:read</code>.
            Dynamiczna rejestracja klientów (RFC7591) <strong>nie</strong> jest wspierana — użyj kluczy wygenerowanych w Kroku 1.
          </Alert>
        </Box>
      </Paper>

      {/* Diagnostics — recent OAuth flow events */}
      <Paper className="mb-6 shadow-sm">
        <Button
          fullWidth
          onClick={() => setAuditOpen((v) => !v)}
          endIcon={auditOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: 'none', justifyContent: 'space-between', px: 3, py: 2 }}
        >
          <Box className="flex items-center gap-2">
            <BugReportIcon fontSize="small" />
            <Typography variant="h6" className="font-semibold text-gray-800">
              Diagnostyka — ostatnie zdarzenia OAuth
            </Typography>
          </Box>
        </Button>
        <Collapse in={auditOpen}>
          <Box className="px-4 pb-4">
            <Box className="flex items-center justify-between mb-2">
              <Typography variant="body2" className="text-gray-500">
                Pokazuje ostatnie zdarzenia flow MCP/OAuth (approve, token, consume_code, ...) — pomocne przy diagnozowaniu problemów z podłączeniem connectora.
              </Typography>
              <Button size="small" startIcon={<RefreshIcon />} onClick={loadAudit} disabled={auditLoading}>
                {auditLoading ? 'Ładowanie…' : 'Odśwież'}
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow className="bg-gray-50">
                    <TableCell className="font-semibold">Czas</TableCell>
                    <TableCell className="font-semibold">Zdarzenie</TableCell>
                    <TableCell className="font-semibold">Klient</TableCell>
                    <TableCell className="font-semibold">Status</TableCell>
                    <TableCell className="font-semibold">Szczegóły</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditEvents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 py-4">
                        {auditLoading ? 'Ładowanie…' : 'Brak zdarzeń. Spróbuj podłączyć connector i odśwież.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    auditEvents.map((ev) => (
                      <TableRow key={ev.id} hover>
                        <TableCell className="text-gray-600 text-xs whitespace-nowrap">
                          {new Date(ev.occurred_at).toLocaleString('pl-PL')}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-gray-100 px-1 rounded">{ev.event}</code>
                        </TableCell>
                        <TableCell className="text-xs text-gray-600">
                          {ev.client_name || ev.client_id || '—'}
                        </TableCell>
                        <TableCell>
                          {ev.success ? (
                            <Chip size="small" label="OK" color="success" />
                          ) : (
                            <Chip size="small" label="FAIL" color="error" />
                          )}
                        </TableCell>
                        <TableCell>
                          <pre className="text-xs bg-gray-50 p-1 rounded m-0 max-w-[480px] whitespace-pre-wrap break-all">
                            {JSON.stringify(ev.details, null, 0)}
                          </pre>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Collapse>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Utwórz nowego klienta OAuth</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nazwa połączenia"
            fullWidth
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
            placeholder="np. Claude AI — Laptop MacBook"
            helperText="Ta nazwa będzie widoczna na ekranie zgody (consent screen)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Anuluj</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!newClientName.trim()}>
            Utwórz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created Client Credentials Dialog */}
      <Dialog open={!!createdClient} onClose={() => setCreatedClient(null)} maxWidth="sm" fullWidth>
        <DialogTitle className="text-green-700">✅ Klient utworzony</DialogTitle>
        <DialogContent>
          <Alert severity="warning" className="mb-3">
            <strong>Client Secret jest wyświetlany tylko teraz.</strong> Skopiuj go i zapisz w bezpiecznym miejscu — nie będzie można go później odzyskać.
          </Alert>
          <Stack gap={2}>
            <CopyableField
              label="OAuth Client ID"
              value={createdClient?.client_id || ''}
              fieldKey="new-id"
              copiedField={copiedField}
              onCopy={copyToClipboard}
            />
            <CopyableField
              label="OAuth Client Secret"
              value={createdClient?.client_secret || ''}
              fieldKey="new-secret"
              copiedField={copiedField}
              onCopy={copyToClipboard}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedClient(null)} variant="contained">
            Mam zapisane, zamknij
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
